import { useState, useCallback, useRef } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from '@/hooks/useTranslation';

interface MarkdownCellEditorProps {
  value: unknown;
  ariaLabel?: string;
  onChange: (value: string | null) => void;
  onCommit: () => void;
  onCancel: () => void;
}

export function MarkdownCellEditor({ value, ariaLabel, onChange, onCommit, onCancel }: MarkdownCellEditorProps) {
  const { t } = useTranslation('spreadsheet');
  const [open, setOpen] = useState(true);
  const [localValue, setLocalValue] = useState(value != null ? String(value) : '');
  const cancelledRef = useRef(false);

  const handleTextareaRef = useCallback((el: HTMLTextAreaElement | null) => {
    if (el) {
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, []);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen && !cancelledRef.current) {
      onChange(localValue || null);
      onCommit();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button className="cell-editing-input text-xs text-left truncate" aria-label={ariaLabel}>
          {localValue || <span className="text-muted-foreground">{t('cells.addNote')}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-2" align="start" sideOffset={2}>
        <Textarea
          ref={handleTextareaRef}
          value={localValue}
          onChange={e => setLocalValue(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              e.stopPropagation();
              cancelledRef.current = true;
              onCancel();
              setOpen(false);
            }
            // Ctrl/Cmd+Enter to save
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
              e.preventDefault();
              onChange(localValue || null);
              onCommit();
              setOpen(false);
            }
          }}
          placeholder={t('cells.enterNote')}
          className="min-h-[100px] text-xs resize-none"
        />
        <p className="text-[10px] text-muted-foreground mt-1">
          {t('cells.saveHint')}
        </p>
      </PopoverContent>
    </Popover>
  );
}